import { useState } from 'react';
import useSWRInfinite from 'swr/infinite';

import CommunityGridV2 from '../gridV2/CommunityGrid';
import { jsonFetcherWithHandler as fetcher } from 'lib/swr';
import { CommunityGridItem, CommunityRemix, ShotRemix } from 'types/community';

type Props = {
  communityPostId: number;
  remixes: ShotRemix[];
};

function remixItemHandler(data: any) {
  return data.remixes.map((p: any) => CommunityRemix.fromAPI(p).toShotRemix());
}

function getCommunityRemixesUrl(communityPostId: number, before?: number) {
  const params: Record<string, string> = {
    limit: '20',
  };
  if (before) {
    params.before = before.toString();
  }

  const queryString = new URLSearchParams(params).toString();
  const endpoint = `/api/community-posts/${communityPostId}/remixes`;
  return queryString ? `${endpoint}?${queryString}` : endpoint;
}

export default function RemixesGrid({ communityPostId, remixes }: Props) {
  const [hasMoreItems, setHasMoreItems] = useState<boolean>(true);

  function getUrl(pageIndex: Number, previousPageData: ShotRemix[] | null) {
    if (pageIndex === 0) {
      const before = remixes.length
        ? remixes[remixes.length - 1].communityRevisionId
        : 1;
      return getCommunityRemixesUrl(communityPostId, before);
    }

    if (previousPageData === null || previousPageData.length === 0) {
      setHasMoreItems(false);
      return null;
    }

    const before =
      previousPageData[previousPageData.length - 1].communityRevisionId;
    return getCommunityRemixesUrl(communityPostId, before);
  }

  const {
    data: nextData,
    setSize,
    isValidating,
  } = useSWRInfinite(getUrl, fetcher(remixItemHandler), {
    initialSize: 0,
    revalidateFirstPage: false,
  });

  const gridItems: CommunityGridItem[] = (
    nextData ? remixes.concat(...nextData) : remixes
  ).map((remix) => ({
    ...remix,
    urlSlug: remix.slug,
    owner: {
      ownerId: remix.ownerId,
      username: remix.username,
      avatar: remix.avatar,
    },
    tags: [],
    revisionId: 0,
    isRemix: false,
    animation: remix.thumbnailAnimation,
  }));

  function onScrollNearEnd() {
    setSize((size: number) => {
      return size + 1;
    });
  }

  return (
    <CommunityGridV2
      gridItems={gridItems}
      hasMoreItems={hasMoreItems}
      onScrollNearEnd={onScrollNearEnd}
      isValidating={isValidating}
      smallerGrid
    />
  );
}
